import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

// Dental chart was relocated to `src/charts/plugins/dental/` in the
// Widget Library MVP. Sibling primitives (usePatientToothHistory,
// odontogram renderers, etc.) still live in the legacy dental subtree
// under `src/components/procedures/widget/dental/`; the plugin reaches
// back into that folder for them. A future PR can flatten the whole
// dental subtree into this folder.
import usePatientToothHistory, {
  TERMINAL_MARKER_KINDS,
  deriveSittingState,
} from '../../../components/procedures/widget/dental/usePatientToothHistory';
import { markerFor } from '../../../components/procedures/widget/dental/markerMap';
import { getPrescriptionConfig } from '../../../services/api/prescriptionBrandingApi';
import { defaultDentition } from '../../../components/procedures/widget/dental/dentition';
import ToothActionsPanel from './ToothActionsPanel';
import {
  STATUS_META,
  STATUS_PILLS,
  formatRelative,
  humanSittingStatus,
  tokenToHumanLabel,
} from '../../../components/procedures/widget/dental/summaryHelpers';
import { buildSummary } from '../../../components/procedures/widget/dental/summaryProjection';
import { statusFillColor } from '../../../components/procedures/widget/dental/statusFill';
import ProcedureViewPopover from '../../../components/procedures/widget/dental/ProcedureViewPopover';
import RowActionToolbar from '../../../components/procedures/widget/dental/RowActionToolbar';
import eventBus from '../../../services/realtime/EventBus';
import { STORAGE_KEYS } from '../../../constants';
import { Z_CHART_PANE, Z_TOAST, Z_WIDGET_OVERLAY } from '../../../constants/zLayers';
import { useWidgetSDK, useChartDropTarget, subscribeActiveDrag } from '../../../widgets';
import ChartPaneProcedurePicker from './ChartPaneProcedurePicker';
import TabletToothActionPopover from './tablet/TabletToothActionPopover';
import TabletChartSummaryOverlay from './tablet/TabletChartSummaryOverlay';
import usePortraitOrientation from '../../../hooks/usePortraitOrientation';

// F8.2 — read/write the per-dentist auto-open preference. Safe against
// private-mode storage exceptions.
function readAutoOpenPref() {
  try {
    const v = window.localStorage?.getItem(STORAGE_KEYS.AUTO_OPEN_TOOTH_CHART);
    return v == null ? true : v !== 'false';
  } catch (_e) { return true; }
}
function writeAutoOpenPref(on) {
  try {
    window.localStorage?.setItem(
      STORAGE_KEYS.AUTO_OPEN_TOOTH_CHART,
      on ? 'true' : 'false',
    );
  } catch (_e) { /* private mode */ }
}

// Lazy-load the anatomical curved-arch renderer so the dialog's
// initial bundle stays slim.
const Odontogram = lazy(() => import('../../../components/procedures/widget/dental/ArchOdontogram'));

// Render a date string (visit_date / follow_up_date is YYYY-MM-DD or
// ISO timestamp) as a locale-formatted day for the printout. Falls
// back to the raw value if Date parsing fails.
function formatPrintDate(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Per-kind tints used in the print legend swatches. Sourced from
// AnatomicalTooth's HISTORY_TINT so the printed legend matches the
// chart's procedure-history badges.
const PRINT_LEGEND_TINT = {
  rct: '#dc2626', crown: '#7c3aed', bridge: '#0369a1',
  filled: '#0d9488', missing: '#475569',
  fractured: '#b45309', impacted: '#9333ea',
  gum: '#e11d48',
};

// Escape user-supplied strings before inlining into the popup's HTML.
function escapeHtml(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Status label + order used when rendering procedures inside a visit
// group. Planned > In progress > Done puts forward-looking work first,
// matching the on-screen FindingsSummary status banner ordering.
const PRINT_STATE_LABEL = { planned: 'Planned', in_progress: 'In progress', completed: 'Done' };
const PRINT_STATE_ORDER = { planned: 0, in_progress: 1, completed: 2 };

// Format a YYYY-MM-DD / ISO date specifically for the popup. Mirrors
// the in-component formatPrintDate but is module-scoped so the popup
// builder (also module-scoped) can call it without a closure.
function formatPopupDate(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Build the full HTML for the print popup window. Self-contained —
// inline CSS only, no Tailwind dependency, no theme leakage.
//
//   mode: 'fresh' (Recent) | 'full' — controls header right column +
//         which prior-visit content the parent already filtered into
//         visitGroups.
//   visitGroups: ordered (newest-first) array of
//         { visit_id, visit_date, diagnoses, procedures } — each
//         procedures entry carries `state` for the inline status pill.
function buildPrintableHtml({
  mode,
  clinicName, clinicPhone, doctorName,
  patientName, patientAge, visitDate, followUpDate,
  chartSvgHtml, spriteHtml,
  visitGroups, diagnosisLookup,
}) {
  const legendDefs = [
    ['rct', 'Root canal'], ['crown', 'Crown'], ['bridge', 'Bridge'],
    ['filled', 'Filling'], ['missing', 'Missing / extracted'],
    ['fractured', 'Fractured'], ['impacted', 'Impacted'],
    ['gum', 'Gum / periodontal'],
  ];
  const legendHtml = legendDefs.map(([kind, label]) => `
    <span class="legend-item">
      <span class="legend-swatch" style="background:${PRINT_LEGEND_TINT[kind] || '#475569'}"></span>
      <span>${escapeHtml(label)}</span>
    </span>`).join('');

  const renderTeeth = (arr) => (arr && arr.length)
    ? ` — ${escapeHtml(arr.join(', '))}` : '';

  // ─── Visit groups ────────────────────────────────────────────────
  const groups = Array.isArray(visitGroups) ? visitGroups : [];
  const groupsHtml = groups.length
    ? groups.map((g) => {
      const visitLabel = g.visit_date ? formatPopupDate(g.visit_date) : 'Date unknown';
      const diagnosesBlock = g.diagnoses && g.diagnoses.length ? `
        <div class="section">
          <div class="section-title">Diagnoses</div>
          <ul>${g.diagnoses.map((d) =>
            `<li>${escapeHtml(d.content || '')}${renderTeeth(d.tooth_numbers)}</li>`,
          ).join('')}</ul>
        </div>` : '';
      // Sort: planned → in_progress → done
      const sortedProcs = [...(g.procedures || [])].sort((a, b) => (
        (PRINT_STATE_ORDER[a.state] ?? 99) - (PRINT_STATE_ORDER[b.state] ?? 99)
      ));
      const proceduresBlock = sortedProcs.length ? `
        <div class="section">
          <div class="section-title">Procedures</div>
          <ul>${sortedProcs.map((s) => {
            const diagContent = s.linked_diagnosis_metadata_id
              ? diagnosisLookup?.get(s.linked_diagnosis_metadata_id) : null;
            const forClause = diagContent ? ` · for: ${escapeHtml(diagContent)}` : '';
            const statusLabel = PRINT_STATE_LABEL[s.state] || s.state || '';
            const statusClass = s.state ? `status status-${s.state}` : 'status';
            return `<li><span class="${statusClass}">${escapeHtml(statusLabel)}</span>${escapeHtml(s.procedure_name || 'Procedure')}${renderTeeth(s.tooth_numbers)}${forClause}</li>`;
          }).join('')}</ul>
        </div>` : '';
      return `
        <div class="visit-group">
          <div class="visit-heading">Visit: ${escapeHtml(visitLabel)}</div>
          ${diagnosesBlock}
          ${proceduresBlock}
        </div>`;
    }).join('')
    : `<div class="empty-summary">No diagnoses or procedures recorded.</div>`;

  // ─── Header right-column variant ────────────────────────────────
  let headerRight;
  if (mode === 'full') {
    const dated = groups.filter((g) => g.visit_date).map((g) => g.visit_date);
    let rangeLine = '';
    if (dated.length > 0) {
      // groups are sorted newest-first → first = latest, last = earliest
      const latest = formatPopupDate(dated[0]);
      const earliest = formatPopupDate(dated[dated.length - 1]);
      rangeLine = earliest === latest
        ? `<div>${escapeHtml(latest)}</div>`
        : `<div>${escapeHtml(earliest)}&nbsp;–&nbsp;${escapeHtml(latest)}</div>`;
    }
    headerRight = `
      <div class="patient-name">${escapeHtml(patientName || 'Patient')}${patientAge != null ? ` · ${escapeHtml(patientAge)}y` : ''}</div>
      <div><strong>Full history</strong></div>
      ${rangeLine}
      ${followUpDate ? `<div class="next-visit"><strong>Next visit:</strong> ${escapeHtml(followUpDate)}</div>` : ''}
    `;
  } else {
    headerRight = `
      <div class="patient-name">${escapeHtml(patientName || 'Patient')}${patientAge != null ? ` · ${escapeHtml(patientAge)}y` : ''}</div>
      ${visitDate ? `<div>Visit: ${escapeHtml(visitDate)}</div>` : ''}
      ${followUpDate ? `<div class="next-visit"><strong>Next visit:</strong> ${escapeHtml(followUpDate)}</div>` : ''}
    `;
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>Tooth Chart — ${escapeHtml(patientName || 'Patient')}</title>
  <style>
    @page { size: A4; margin: 12mm; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0; padding: 0;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: #0f172a; background: #fff;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .header {
      display: flex; justify-content: space-between; align-items: flex-start;
      gap: 16px; padding-bottom: 8px; margin-bottom: 12px;
      border-bottom: 1px solid #94a3b8;
      page-break-after: avoid;
    }
    .clinic-name { font-size: 16px; font-weight: 700; line-height: 1.2; }
    .clinic-phone { font-size: 10px; color: #475569; margin-top: 2px; }
    .doctor-name { font-size: 11px; color: #334155; margin-top: 4px; }
    .patient-block { text-align: right; font-size: 11px; line-height: 1.4; }
    .patient-name { font-size: 12px; font-weight: 700; }
    .next-visit { margin-top: 4px; }
    .chart-wrap {
      text-align: center; margin: 0 auto;
      page-break-inside: avoid;
    }
    .chart-wrap svg { display: block; max-width: 100%; height: auto; margin: 0 auto; }
    .legend {
      border-top: 1px solid #cbd5e1; padding-top: 8px;
      margin-top: 12px; font-size: 10px;
    }
    .legend-title { font-weight: 600; margin-bottom: 4px; }
    .legend-items { display: flex; flex-wrap: wrap; column-gap: 12px; row-gap: 4px; }
    .legend-item { display: inline-flex; align-items: center; gap: 4px; }
    .legend-swatch {
      width: 8px; height: 8px; border-radius: 50%;
      display: inline-block; flex-shrink: 0;
    }
    .legend-foot {
      margin-top: 4px; display: flex; flex-wrap: wrap;
      column-gap: 12px; color: #475569;
    }
    .summary-title {
      font-size: 14px; font-weight: 600;
      margin: 14px 0 6px;
      page-break-after: avoid;
    }
    .section { margin-bottom: 6px; }
    .section-title {
      font-weight: 600; font-size: 12px;
      page-break-after: avoid;
    }
    ul { margin: 4px 0 0 20px; padding: 0; font-size: 12px; }
    li { page-break-inside: avoid; margin-bottom: 2px; }
    /* Per-visit grouped summary (Phase 7). Each visit's heading +
       findings stay together when possible; a long visit may still
       break across pages but the heading never orphans. */
    .visit-group {
      margin-top: 10px;
      page-break-inside: avoid;
    }
    .visit-heading {
      font-weight: 700; font-size: 12px;
      border-bottom: 1px solid #e2e8f0;
      padding-bottom: 2px;
      margin-bottom: 4px;
      page-break-after: avoid;
    }
    .empty-summary {
      font-size: 11px; color: #94a3b8;
      font-style: italic; margin-top: 8px;
    }
    /* Inline procedure-status pill. Backgrounds need print-color-adjust
       to survive the default "Background graphics off" setting. */
    .status {
      display: inline-block;
      font-size: 9px; font-weight: 600;
      padding: 1px 5px; border-radius: 3px;
      margin-right: 5px;
      vertical-align: 1px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .status-planned { background: #dbeafe; color: #1e40af; }
    .status-in_progress { background: #ccfbf1; color: #115e59; }
    .status-completed { background: #dcfce7; color: #166534; }
  </style>
</head>
<body>
  ${spriteHtml || ''}
  <div class="header">
    <div>
      ${clinicName ? `<div class="clinic-name">${escapeHtml(clinicName)}</div>` : ''}
      ${clinicPhone ? `<div class="clinic-phone">${escapeHtml(clinicPhone)}</div>` : ''}
      ${doctorName ? `<div class="doctor-name">Dr. ${escapeHtml(doctorName)}</div>` : ''}
    </div>
    <div class="patient-block">${headerRight}</div>
  </div>

  <div class="chart-wrap">
    ${chartSvgHtml || '<div style="font-size:11px;color:#94a3b8;font-style:italic;">Chart unavailable</div>'}
  </div>

  <div class="legend">
    <div class="legend-title">Legend</div>
    <div class="legend-items">${legendHtml}</div>
    <div class="legend-foot">
      <span>○ planned · ● done</span>
      <span>· Tooth numbering: FDI (ISO 3950)</span>
    </div>
  </div>

  <div class="summary-title">Summary${mode === 'full' ? ' — Full history' : ''}</div>
  ${groupsHtml}
</body>
</html>`;
}

function LegendItem({ kind, label }) {
  const tint = PRINT_LEGEND_TINT[kind] || '#475569';
  // SVG circle (not a CSS background) so the swatch survives the
  // print-time `background-color: transparent !important` reset
  // applied to the rest of the chart pane.
  return (
    <span className="inline-flex items-center gap-1">
      <svg width="8" height="8" aria-hidden="true" style={{ flexShrink: 0 }}>
        <circle cx="4" cy="4" r="4" fill={tint} />
      </svg>
      <span>{label}</span>
    </span>
  );
}

/**
 * Phase 3.1.1 + 3.1.2 — ToothChartDialog (multi-select + gum-mode aware).
 *
 * Props:
 *   visit              — visit row (patient_id + patient_age)
 *   viewer             — { memberId, role }
 *   pendingTokens      — string[] of tokens being added in the parent's
 *                        add-procedure flow (renders as outline-only)
 *   initialActiveToken — optional FDI/quadrant/FM token; auto-opens the
 *                        panel for that token.
 *   onClose            — () => void
 *   isDark             — boolean
 */
export default function ToothChartContainer({
  visit,
  viewer,
  pendingTokens = [],
  initialActiveToken = null,
  onClose,
  isDark = false,
  // Layout mode.
  //  - `'pane'` (default) keeps today's centered chart pane that leaves
  //    room for the right-edge widget rail (desktop dentist view).
  //  - `'fullBleed'` covers the entire viewport at Z_WIDGET_OVERLAY —
  //    legacy chairside-on-tablet branch; the rail is intentionally
  //    covered.
  //  - `'tabletPaired'` (Variant M) is the new chairside default: chart
  //    sits left of the rail with a responsive right-gap (368 px
  //    landscape / 296 px portrait / 0 px phone, with bottom-sheet
  //    rail), driving the `📋 Summary` button + rose close pill in the
  //    header. Z-index stays at Z_CHART_PANE so the paired rail above
  //    it stays clickable.
  presentation = 'pane',
  // Variant M — Summary overlay toggle. Wired by TabletDentalChartHost
  // from the tablet chart store. Ignored when presentation !== 'tabletPaired'.
  summaryOpen = false,
  onSummaryToggle = null,
}) {
  const isPortrait = usePortraitOrientation();
  const patientId = visit?.patient_id;
  const currentVisitId = visit?.visit_id || null;
  const {
    markers,
    entriesByToken,
    notesByToken,
    diagnosesByVisit,
    proceduresByVisit,
    visits,
    refresh,
    addOptimisticEntry,
    addOptimisticSitting,
  } = usePatientToothHistory(patientId, currentVisitId);

  // ONE chart — all history, always. The Recent/Full mode toggle (and its
  // 150-day "fresh" filter, banner, and greying of older work) was removed:
  // procedures and notes are now fetched complete (plans + the patient
  // metadata index), so there's nothing to "show more" of. `chartMode` is
  // kept as a constant so the (now-trivial) downstream references read clearly.
  const chartMode = 'full';

  // Phase 6 — keep a live ref to selectedTokens so the drop handlers
  // (defined here, before the selectedTokens useState below) can read
  // the latest multi-selection at drop-event time. Sync effect runs
  // AFTER selectedTokens is declared further down.
  const selectedTokensRef = useRef([]);

  // Variant M (tabletPaired) — anchor the floating tooth-action popover
  // to this ref. Wraps the Odontogram column so the popover can read
  // each tooth's bbox relative to the host.
  const archHostRef = useRef(null);

  // Phase 7 — inline toast for drop-flow feedback (multi-selection
  // misses, no-selection drop, etc.). Auto-dismisses after 3.5s.
  // Lives at the top of the chart pane so the dentist's eye stays
  // near the action they just attempted.
  const [paneToast, setPaneToast] = useState(null); // { text, tone: 'warn' | 'info' } | null
  const paneToastTimerRef = useRef(null);
  const showPaneToast = useCallback((text, tone = 'warn') => {
    setPaneToast({ text, tone });
    if (paneToastTimerRef.current) clearTimeout(paneToastTimerRef.current);
    paneToastTimerRef.current = setTimeout(() => setPaneToast(null), 3500);
  }, []);
  useEffect(() => () => {
    if (paneToastTimerRef.current) clearTimeout(paneToastTimerRef.current);
  }, []);

  // Phase 7 — floating cursor chip. Subscribes to the shared active-
  // drag bus + listens for dragover globally. On each move, computes
  // "what will happen if I release here" so the dentist sees the
  // outcome BEFORE the drop. Tone follows the same rules the drop
  // handlers use, so the chip and the actual save match.
  //   - Multi-selection + cursor on selected tooth / empty pane →
  //       "Adds X to N teeth: 14, 15, 16"
  //   - Multi-selection + cursor on tooth NOT in selection →
  //       "⚠ Tooth #17 is outside your selection"
  //   - Single selection + cursor on a tooth → "Adds X to tooth #N"
  //   - No selection + cursor on a tooth → "Adds X to tooth #N"
  //   - No selection + cursor on empty pane → "Select teeth first"
  const [dragHint, setDragHint] = useState(null); // { text, x, y, tone } | null
  useEffect(() => {
    let active = null;
    const unsub = subscribeActiveDrag((payload) => {
      active = payload;
      if (!payload) setDragHint(null);
    });

    const onMove = (e) => {
      if (!active || active.type !== 'procedure') return;

      const sel = selectedTokensRef.current || [];
      const hasMulti = Array.isArray(sel) && sel.length > 1;

      // Identify what's under the cursor
      const target = (typeof document !== 'undefined')
        ? document.elementFromPoint(e.clientX, e.clientY)
        : null;
      const toothEl = target?.closest?.('[data-tooth-fdi]');
      const summaryRow = target?.closest?.('.arogya-summary-drop-row');

      const procName = active.procedureName || active.procedureId || 'procedure';
      let text;
      let tone = 'info';

      if (summaryRow) {
        // Hovering a diagnosis row OR FindingsSummary row — the row's
        // own "Links to: ..." chip provides specifics; keep this chip
        // short so they don't fight for attention.
        text = `→ Drop to link ${procName}`;
      } else if (hasMulti && toothEl) {
        const fdi = toothEl.getAttribute('data-tooth-fdi');
        if (fdi && sel.includes(fdi)) {
          text = `Adds ${procName} to ${sel.length} teeth: #${sel.join(', #')}`;
        } else {
          text = `⚠ Tooth #${fdi} is outside your selection`;
          tone = 'warn';
        }
      } else if (hasMulti) {
        // Multi-select active, hovering empty pane area
        text = `Adds ${procName} to ${sel.length} teeth: #${sel.join(', #')}`;
      } else if (toothEl) {
        const fdi = toothEl.getAttribute('data-tooth-fdi');
        text = `Adds ${procName} to tooth #${fdi}`;
      } else {
        text = 'Select teeth first, or drop on a tooth';
        tone = 'warn';
      }

      setDragHint({ text, x: e.clientX, y: e.clientY, tone });
    };

    const onLeave = (e) => {
      // dragleave fires on element transitions too; only clear when
      // leaving the whole window (relatedTarget null means cursor
      // left the document).
      if (!e.relatedTarget) setDragHint(null);
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('dragover', onMove);
      document.addEventListener('dragleave', onLeave);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('dragover', onMove);
        document.removeEventListener('dragleave', onLeave);
      }
      unsub();
    };
  }, []);

  // ── Widget SDK bridge ──────────────────────────────────────────
  // Subscribe to the widget router store so the chart can:
  //   - Auto-minimize / de-expand when a procedure widget opens (Bug D)
  //   - Host the inline procedure picker in its right-side portal
  //     target when placement === 'chart-pane' (Phase 2.C)
  const sdk = useWidgetSDK();
  const activeWidget = sdk.activeWidget;
  const isChartPaneProcedureActive = (
    activeWidget?.kind === 'procedure'
    && activeWidget.opts?.placement === 'chart-pane'
    && activeWidget.context?.visitId === visit?.visit_id
  );

  useEffect(() => {
    if (!activeWidget) return;
    if (activeWidget.kind !== 'procedure') return;
    if (activeWidget.context?.visitId !== visit?.visit_id) return;
    // Yield the screen for the rail-placement path so the rail can
    // expand into a visible area. For chart-pane placement, we KEEP
    // the chart open — the picker mounts inline next to the tooth
    // chart UI, no eye travel.
    if (activeWidget.opts?.placement !== 'chart-pane') {
      setExpanded(false);
    }
  }, [activeWidget, visit?.visit_id]);

  // ── Procedure drag-drop bridge (Phase 3) ──────────────────────
  // Called by AnatomicalTooth when a procedure payload is dropped on
  // a tooth. Dental-specific behaviour: look up the latest diagnosis
  // on the dropped-on tooth, compose the auto-note summary, and open
  // ChartPaneProcedurePicker pre-filled with everything. The picker
  // itself is dental's component (already wired in Phase 2.C); we
  // just hand it richer prefill data here.
  const handleProcedureDrop = useCallback((payload, fdi) => {
    if (!visit?.visit_id) return;
    if (!payload || payload.type !== 'procedure') return;
    const fdiToken = String(fdi);

    // Phase 7 — "multi-selection always wins". If the dentist has an
    // active multi-selection, EVERY drop in the chart pane uses the
    // whole selection. Drops on teeth NOT in the selection are
    // intercepted with a toast instead of silently switching to
    // single-tooth (the previous behaviour surprised dentists).
    const currentSelection = selectedTokensRef.current || [];
    const hasMultiSelection = (
      Array.isArray(currentSelection) && currentSelection.length > 1
    );

    if (hasMultiSelection && !currentSelection.includes(fdiToken)) {
      showPaneToast(
        `Tooth #${fdiToken} is outside your selection — click it to add, or drop on a selected tooth.`,
      );
      return;
    }

    const tokens = hasMultiSelection ? [...currentSelection] : [fdiToken];
    const isPartOfSelection = hasMultiSelection;

    // Look up latest diagnosis on the dropped-on tooth (or first
    // token of a multi-selection) via the existing notesByToken index.
    const lookupToken = isPartOfSelection ? tokens[0] : fdiToken;
    const diagnosesHere = (notesByToken?.get?.(lookupToken) || [])
      .filter((n) => (n.kind || '').toLowerCase() === 'diagnosis');
    const latestDx = diagnosesHere[0] || null;
    const baseSummary = latestDx?.content || null;
    const toothLabel = tokens.length === 1
      ? `Tooth #${tokens[0]}`
      : `Teeth #${tokens.join(', #')}`;
    const composedSummary = baseSummary
      ? `${toothLabel} · ${baseSummary}`
      : toothLabel;

    const requestId = (
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `drag-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    );

    sdk.openWidget(
      'procedure',
      {
        visitId: visit.visit_id,
        anatomicalRef: {
          kind: 'dental-tooth-surface',
          payload: { tokens },
          summary: toothLabel,
        },
        prefill: {
          procedureId:     payload.procedureId || null,
          procedureName:   payload.procedureName || null,
          tokens,
          diagnosisMetaId: latestDx?.meta_id || null,
          summary:         composedSummary,
          requestId,
        },
      },
      {
        source: 'drag-drop',
        placement: 'chart-pane',
        requestId,
      },
    );
  }, [sdk, visit, notesByToken, showPaneToast]);

  // Phase 6 — broad chart-pane drop zone handler. Fires only when the
  // drop happens on chart-pane area that's NOT claimed by a per-tooth
  // / per-row child handler (child onDrop calls e.stopPropagation()).
  // Uses the live multi-selection from the ref; no-op when no
  // selection exists (broad handler is also `disabled` in that case).
  const handleBroadProcedureDrop = useCallback((payload) => {
    if (!visit?.visit_id) return;
    if (!payload || payload.type !== 'procedure') return;
    const tokens = selectedTokensRef.current || [];
    // Phase 7 — when the dentist drops on empty pane area with no
    // selection active, give them a hint instead of silently doing
    // nothing.
    if (!Array.isArray(tokens) || tokens.length === 0) {
      showPaneToast(
        'Select one or more teeth first, then drop the procedure here. Or drop directly on a tooth.',
        'info',
      );
      return;
    }

    const firstToken = String(tokens[0]);
    const diagnosesHere = (notesByToken?.get?.(firstToken) || [])
      .filter((n) => (n.kind || '').toLowerCase() === 'diagnosis');
    const latestDx = diagnosesHere[0] || null;
    const toothLabel = tokens.length === 1
      ? `Tooth #${tokens[0]}`
      : `Teeth #${tokens.join(', #')}`;
    const composedSummary = latestDx?.content
      ? `${toothLabel} · ${latestDx.content}`
      : toothLabel;

    const requestId = (
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `drag-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    );

    sdk.openWidget(
      'procedure',
      {
        visitId: visit.visit_id,
        anatomicalRef: { kind: 'dental-tooth-surface', payload: { tokens }, summary: toothLabel },
        prefill: {
          procedureId:     payload.procedureId || null,
          procedureName:   payload.procedureName || null,
          tokens,
          diagnosisMetaId: latestDx?.meta_id || null,
          summary:         composedSummary,
          requestId,
        },
      },
      {
        source: 'drag-drop-broad',
        placement: 'chart-pane',
        requestId,
      },
    );
  }, [sdk, visit, notesByToken, showPaneToast]);

  // One chart, all history — no Recent-mode marker filtering. Every tooth
  // with any marker shows.
  const baseFilteredMarkers = markers;

  // C4 — full-mode toggle. In expanded, the chart fills the viewport
  // and the right-side findings panel collapses behind a "Summary"
  // slide-over button.
  const [expanded, setExpanded] = useState(false);
  // F8.4 — per-dentist auto-open preference, synced to localStorage.
  const [autoOpenChart, setAutoOpenChart] = useState(() => readAutoOpenPref());
  // F8.6 — workflow hint banner. Shows on first open for patients with
  // no existing findings, dismisses after the dentist clicks any tooth
  // OR explicitly closes the banner. Remembered per-patient.
  const hintSeenKey = patientId
    ? `${STORAGE_KEYS.CHART_HINT_SEEN}:${patientId}`
    : null;
  const [hintDismissed, setHintDismissed] = useState(() => {
    if (!hintSeenKey) return true;
    try { return window.localStorage?.getItem(hintSeenKey) === '1'; }
    catch (_e) { return true; }
  });
  const dismissHint = () => {
    setHintDismissed(true);
    if (!hintSeenKey) return;
    try { window.localStorage?.setItem(hintSeenKey, '1'); }
    catch (_e) { /* private mode */ }
  };

  // Dev-only timer to validate the C1/C2/C4 perf wins during smoke.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return undefined;
    // eslint-disable-next-line no-console
    console.time('tooth-chart-paint');
    return () => {
      try {
        // eslint-disable-next-line no-console
        console.timeEnd('tooth-chart-paint');
      } catch (_e) { /* timer already ended elsewhere */ }
    };
  }, []);

  const [dentitionOverride, setDentitionOverride] = useState(null);
  const dentitionMode = (
    dentitionOverride
    || defaultDentition(visit?.patient_age)
  );

  // Default 'multi' — dentists routinely dictate findings on multiple
  // teeth in one sitting (eg. "16, 17, 26 all need RCT"). The Single /
  // Multi toggle in HeaderControls lets a clinic flip back.
  const [selectionMode, setSelectionMode] = useState('multi');
  // Surface mode (tooth | gum)
  const [surfaceMode, setSurfaceMode] = useState('tooth');

  const [selectedTokens, setSelectedTokens] = useState(
    initialActiveToken ? [initialActiveToken] : [],
  );

  // Phase 6 — keep `selectedTokensRef` (declared earlier, before the
  // drop handlers) in sync with the latest selection so the handlers
  // read a fresh value at drop-event time without needing the state
  // in their useCallback dependency arrays.
  useEffect(() => {
    selectedTokensRef.current = selectedTokens;
  }, [selectedTokens]);

  // Quadrant focus — the arch renderer reports back which quadrants
  // (Set of Q1/Q2/Q3/Q4) hold ≥ 4 of the current selection. Driving
  // this off a Set instead of a single string fixes the bilateral-work
  // case where two quadrants both cross the threshold (e.g. 4 teeth
  // in Q1 + 4 in Q3) — both light up instead of one silently winning.
  const [focusedQuadrants, setFocusedQuadrants] = useState(() => new Set());

  // activeQuadrants ∪ fmActive — union of (auto-focused set) and any
  // Q1/Q2/Q3/Q4/FM tokens explicitly selected via chip clicks. All
  // four Q's active is treated as full-mouth scope (one outer ring
  // instead of four per-quadrant rings).
  const { activeQuadrants, fmActive } = useMemo(() => {
    const set = new Set(focusedQuadrants);
    let fm = false;
    for (const tok of selectedTokens) {
      const stripped = typeof tok === 'string' && tok.startsWith('gum:')
        ? tok.slice(4) : tok;
      if (stripped === 'FM') fm = true;
      else if (stripped === 'Q1' || stripped === 'Q2'
        || stripped === 'Q3' || stripped === 'Q4') set.add(stripped);
    }
    if (set.size === 4) fm = true;
    return { activeQuadrants: set, fmActive: fm };
  }, [focusedQuadrants, selectedTokens]);

  // Per-scope procedure status — roll up entries on Q1/Q2/Q3/Q4/FM
  // tokens (and their gum: variants) to a single status with the same
  // precedence the chart uses for teeth: in_progress > planned >
  // completed > null. Only sitting rows count; notes/diagnoses on a
  // quadrant don't draw a status ring (they don't carry a status).
  const { quadrantStatusByQ, fmStatus } = useMemo(() => {
    const rollup = (entries) => {
      if (!Array.isArray(entries)) return null;
      let best = null;
      for (const e of entries) {
        if (!e?.sitting_id) continue;
        const s = deriveSittingState(e?.status);
        if (s === 'in_progress') return 'in_progress';
        if (s === 'planned' && best !== 'planned') best = 'planned';
        else if (s === 'completed' && best === null) best = 'completed';
      }
      return best;
    };
    const combine = (a, b) => {
      if (a === 'in_progress' || b === 'in_progress') return 'in_progress';
      if (a === 'planned' || b === 'planned') return 'planned';
      if (a === 'completed' || b === 'completed') return 'completed';
      return null;
    };
    const out = { Q1: null, Q2: null, Q3: null, Q4: null };
    for (const q of ['Q1', 'Q2', 'Q3', 'Q4']) {
      out[q] = combine(
        rollup(entriesByToken?.get?.(q)),
        rollup(entriesByToken?.get?.(`gum:${q}`)),
      );
    }
    const fm = combine(
      rollup(entriesByToken?.get?.('FM')),
      rollup(entriesByToken?.get?.('gum:FM')),
    );
    return { quadrantStatusByQ: out, fmStatus: fm };
  }, [entriesByToken]);

  useEffect(() => {
    if (initialActiveToken) {
      setSelectedTokens([initialActiveToken]);
      if (initialActiveToken.startsWith('gum:')) setSurfaceMode('gum');
    }
  }, [initialActiveToken]);

  // K5 — initial-state sync. When the chart opens, tell every store
  // (dentalHistoryStore, sittingStore) to re-confirm freshness from
  // the server. Without this, the Procedures widget below the chart
  // can show data from a stale cache that pre-dates whatever happened
  // on a different tab or before a WS reconnect.
  // dentalHistoryStore already lists TOOTH_CHART_REFRESH in its
  // INVALIDATE_EVENTS (will refetch the bundle). sittingStore picks
  // it up via the handler added in this round.
  useEffect(() => {
    const pid = visit?.patient_id;
    const vid = visit?.visit_id;
    if (!pid) return undefined;
    try {
      // EventBus contract: `emit({ type, payload })` — NOT
      // `emit(name, payload)`. The string form is silently dropped by
      // the bus's allowlist validation.
      eventBus.emit({
        type: 'TOOTH_CHART_REFRESH',
        payload: { patient_id: pid, visit_id: vid },
      });
    } catch (_e) { /* SSR / event bus init race */ }
    return undefined;
  }, [visit?.patient_id, visit?.visit_id]);

  // Wrap a clicked FDI-or-surface in the current scope. In gum mode we
  // prefix `gum:` (gum: tooth or gum: tooth-surface). Otherwise pass
  // through unchanged. Memoised so the click-handler useCallbacks
  // below stay referentially stable across renders (only invalidates
  // when surfaceMode flips).
  const tokenize = useCallback(
    (raw) => (surfaceMode === 'gum' ? `gum:${raw}` : raw),
    [surfaceMode],
  );

  // ── Click handlers — `useCallback`'d so the props passed down to
  //    the memoized <ToothSVG> children stay referentially stable
  //    across renders. Without this, React.memo(ToothSVG) is defeated
  //    on every parent state change because the handler prop is a
  //    fresh closure every time. With it, clicking tooth 16 only
  //    re-renders tooth 16 (~18 SVG nodes) instead of the full chart
  //    (~576 nodes).

  const handleToothClick = useCallback((fdi) => {
    const token = tokenize(fdi);
    setSelectedTokens((prev) => {
      if (selectionMode === 'multi') {
        return prev.includes(token)
          ? prev.filter((t) => t !== token)
          : [...prev, token];
      }
      return [token];
    });
    // F8.6 — first interaction dismisses the workflow hint banner.
    if (!hintDismissed) dismissHint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode, tokenize, hintDismissed]);

  const handleQuadrantClick = useCallback((qToken) => {
    const t = tokenize(qToken);
    setSelectedTokens((prev) => (
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    ));
  }, [tokenize]);

  // FM chip toggle — adds/removes the explicit "FM" token. The
  // container's existing fmActive derivation (size===4 ∪ contains "FM")
  // keeps the ring + chip visuals coherent regardless of whether the
  // dentist entered FM via four Q chips or via this single FM chip.
  // Submit-side (ToothActionsPanel) consolidates to ["FM"] either way
  // so the persisted row carries a single full-mouth token.
  const handleFmClick = useCallback(() => {
    setSelectedTokens((prev) => (
      prev.includes('FM')
        ? prev.filter((t) => t !== 'FM')
        : [...prev, 'FM']
    ));
  }, []);

  const clearSelection = () => setSelectedTokens([]);
  const removeOneToken = (token) => {
    setSelectedTokens((prev) => prev.filter((t) => t !== token));
  };

  const displayMarkers = useMemo(() => {
    // C6: start from the freshness-filtered base instead of raw markers
    // so fresh-mode hides pure-history diagnoses without touching the
    // pending-token overlay logic.
    const m = new Map(baseFilteredMarkers);
    for (const token of pendingTokens || []) {
      if (!m.has(token)) {
        m.set(token, {
          kind: 'filled',
          count: 1,
          source: 'this-clinic',
          pending: true,
        });
      }
    }
    return m;
  }, [baseFilteredMarkers, pendingTokens]);

  // Status counts for the Variant M (tabletPaired) summary overlay's
  // filter pills. Derived from the SAME `buildSummary` selector that
  // FindingsSummary uses, so the overlay's pill counts and the inner
  // findings list always agree — and never fall back to the old
  // all-zeros default that hid the desktop-vs-mobile drift.
  const tabletSummary = useMemo(() => buildSummary({
    entriesByToken, notesByToken, markers: displayMarkers,
  }), [entriesByToken, notesByToken, displayMarkers]);

  // G — the status filter drives BOTH the summary list AND the chart. Lifted
  // here so the desktop side summary, the tablet overlay, and the odontogram
  // all share one value (desktop + tablet stay in sync).
  const [statusFilter, setStatusFilter] = useState('all');

  // G — per-FDI status set built from the SAME projection the list uses, so
  // the chart highlights exactly the teeth the list shows. A tooth matches the
  // active filter if ANY of its tokens (tooth / surface / gum) has that status.
  const fdiStatusSet = useMemo(() => {
    const m = new Map();
    for (const section of tabletSummary.sections || []) {
      for (const it of section.items || []) {
        const fdi = String(it.token || '').replace(/^gum:/, '').split('-')[0];
        if (!fdi) continue;
        if (!m.has(fdi)) m.set(fdi, new Set());
        m.get(fdi).add(it.status);
      }
    }
    return m;
  }, [tabletSummary]);

  const panelOpen = selectedTokens.length > 0;
  const isSinglePanel = selectionMode === 'single' && selectedTokens.length === 1;
  const lookupToken = isSinglePanel ? selectedTokens[0] : null;
  const procedureEntries = lookupToken
    ? (entriesByToken?.get(lookupToken) || [])
    : [];
  const noteEntries = lookupToken
    ? (notesByToken?.get(lookupToken) || [])
    : [];

  // Per-visit count surfaced in the print-only summary header for the
  // Diagnoses section. Other section counts are derived inline from
  // their filtered arrays (plannedProcedures, completedProcedures).
  const visitId = visit?.visit_id;
  const diagnosisCount = (diagnosesByVisit?.get(visitId) || []).length;

  // ─── Printable handout derivations ─────────────────────────────────
  // The printout serves three audiences (dental grads, patients, next-
  // doctor handoff) with one stylesheet. Compute partitions + lookup
  // maps here so the JSX stays declarative.
  const visitProcedures = useMemo(
    () => proceduresByVisit?.get(visitId) || [],
    [proceduresByVisit, visitId],
  );
  const plannedProcedures = useMemo(() => visitProcedures.filter((s) => {
    const st = deriveSittingState(s?.status);
    return st === 'planned' || st === 'in_progress';
  }), [visitProcedures]);
  const completedProcedures = useMemo(() => visitProcedures.filter(
    (s) => deriveSittingState(s?.status) === 'completed',
  ), [visitProcedures]);
  // Map<diagnosis_meta_id, content> built from every visit's diagnoses
  // so a procedure linked to a diagnosis recorded on a prior visit
  // still resolves on the printout.
  const diagnosisContentByMetaId = useMemo(() => {
    const m = new Map();
    if (diagnosesByVisit) {
      for (const list of diagnosesByVisit.values()) {
        for (const d of list || []) {
          if (d?.meta_id && d?.content) m.set(d.meta_id, d.content);
        }
      }
    }
    return m;
  }, [diagnosesByVisit]);

  // Per-visit roll-up consumed by the print popup. Each group:
  //   { visit_id, visit_date, diagnoses, procedures }
  // where every `procedures` entry carries a `state` field driven by
  // deriveSittingState (planned / in_progress / completed). Visits
  // are sorted newest-first.
  //
  // The mode filter mirrors the on-screen Recent / Full toggle:
  //   - 'full'  → every non-empty visit
  //   - 'fresh' → current visit verbatim, prior visits trimmed to
  //               procedures that are still load-bearing (open work
  //               or terminal anatomy); prior-visit diagnoses are
  //               dropped because the chart's marker filter already
  //               surfaces them visually.
  const printVisitGroups = useMemo(() => {
    if (!Array.isArray(visits) || visits.length === 0) return [];
    const groups = visits.map((v) => {
      const vid = v.visit_id;
      const diagnosesList = diagnosesByVisit?.get(vid) || [];
      const sittings = proceduresByVisit?.get(vid) || [];
      const procedures = sittings
        .map((s) => ({ ...s, state: deriveSittingState(s?.status) }))
        .filter((p) => p.state); // drop cancelled / voided
      return {
        visit_id: vid,
        visit_date: v.visit_date || null,
        diagnoses: diagnosesList,
        procedures,
      };
    });

    groups.sort((a, b) => String(b.visit_date || '').localeCompare(String(a.visit_date || '')));

    if (chartMode === 'full') {
      return groups.filter((g) => g.diagnoses.length > 0 || g.procedures.length > 0);
    }

    return groups
      .map((g) => {
        if (g.visit_id === currentVisitId) return g;
        const trimmed = g.procedures.filter((p) => {
          if (p.state === 'planned' || p.state === 'in_progress') return true;
          if (p.state !== 'completed') return false;
          const kind = markerFor(
            p.procedure_snapshot || { name: p.procedure_name, specialty: p.procedure_specialty },
          );
          return TERMINAL_MARKER_KINDS.has(kind);
        });
        return { ...g, diagnoses: [], procedures: trimmed };
      })
      .filter((g) => g.diagnoses.length > 0 || g.procedures.length > 0);
  }, [visits, diagnosesByVisit, proceduresByVisit, chartMode, currentVisitId]);

  // Clinic branding (name + phone) fetched once on mount. Falls back
  // silently — print just renders patient + doctor without clinic
  // letterhead if the org has no Rx branding configured.
  const [clinicBranding, setClinicBranding] = useState({ name: '', phone: '' });
  useEffect(() => {
    let cancelled = false;
    getPrescriptionConfig().then((res) => {
      if (cancelled) return;
      const cfg = res?.config || res || {};
      setClinicBranding({
        name: cfg.clinic_name || '',
        phone: cfg.clinic_phone || '',
      });
    }).catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, []);

  // Print handler — opens a clean popup window with a self-contained
  // print document. Going through a popup (rather than window.print()
  // on the live page) gets us:
  //   - Forced light-mode colors regardless of the dentist's theme
  //   - No surrounding ManageVisitPage chrome bleeding in
  //   - No browser-printed URL/footer (popup URL is about:blank)
  //   - A page-controllable @page size + margin (A4 / A5 / A6 reflow
  //     just works via the browser's paper-size picker)
  const handlePrintChart = useCallback(() => {
    if (typeof window === 'undefined') return;
    // Clone the live chart SVG so the popup gets the current visual
    // state (markers, selections, dentition mode). Prefer the arch
    // odontogram's root if present; fall back to any SVG inside the
    // chart pane.
    const paneEl = document.querySelector('.arogya-chart-pane');
    let svgEl = paneEl?.querySelector('.arogya-arch-root')
      || paneEl?.querySelector('svg');
    let chartSvgHtml = '';
    if (svgEl) {
      const clone = svgEl.cloneNode(true);
      // Drop any interactive-only attributes that would error in a
      // detached document or look noisy on the printed page.
      clone.removeAttribute('data-drop-active');
      clone.querySelectorAll('[data-drop-active]').forEach((n) => n.removeAttribute('data-drop-active'));
      chartSvgHtml = clone.outerHTML;
    }

    // The chart's tooth bodies are rendered via <use href="#tooth-molar">
    // references to a shared sprite mounted at the app root
    // (App.js -> ToothAnatomySprite). In a detached popup document
    // those references resolve to nothing — the result is a chart
    // with procedure outlines but no tooth shapes. Locate the sprite
    // SVG in the live DOM and inline its outerHTML at the top of the
    // popup body so the <use> references resolve there too.
    let spriteHtml = '';
    const symbolEl = document.getElementById('tooth-molar');
    if (symbolEl) {
      const spriteRoot = symbolEl.closest('svg');
      if (spriteRoot) {
        const spriteClone = spriteRoot.cloneNode(true);
        // Force the sprite to be invisible-but-present in the popup
        // (matches App.js's 0×0 positioning).
        spriteClone.setAttribute('aria-hidden', 'true');
        spriteClone.setAttribute('focusable', 'false');
        spriteClone.setAttribute('style', 'position:absolute;width:0;height:0;overflow:hidden;');
        spriteHtml = spriteClone.outerHTML;
      }
    }

    const html = buildPrintableHtml({
      mode: chartMode, // 'fresh' (Recent) | 'full'
      clinicName: clinicBranding.name,
      clinicPhone: clinicBranding.phone,
      doctorName: visit?.doctor_name || '',
      patientName: visit?.patient_name || '',
      patientAge: visit?.patient_age,
      visitDate: visit?.visit_date ? formatPrintDate(visit.visit_date) : '',
      followUpDate: visit?.follow_up_date ? formatPrintDate(visit.follow_up_date) : '',
      chartSvgHtml,
      spriteHtml,
      visitGroups: printVisitGroups,
      diagnosisLookup: diagnosisContentByMetaId,
    });

    const win = window.open('', '_blank', 'width=900,height=1200');
    if (!win) {
      // Popup blocked — surface a friendly toast and fall back to
      // native print on the live page (still useful for dentists who
      // prefer Cmd+P after dismissing the popup-blocker prompt).
      showPaneToast('Pop-up blocked — allow pop-ups for this site to print.', 'warn');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    // Wait for the SVG + fonts to settle before firing print. The
    // popup's onload generally fires after document.close + paint.
    const trigger = () => {
      try {
        win.focus();
        win.print();
      } catch (_e) { /* user closed window mid-flight */ }
    };
    win.onafterprint = () => { try { win.close(); } catch (_e) {} };
    if (win.document.readyState === 'complete') {
      setTimeout(trigger, 100);
    } else {
      win.onload = () => setTimeout(trigger, 100);
    }
  }, [
    chartMode, clinicBranding, visit,
    printVisitGroups, diagnosisContentByMetaId,
    showPaneToast,
  ]);

  // Phase 6 — broad chart-pane drop zone. Accepts a procedure drop
  // anywhere inside the pane that's NOT claimed by a specific child
  // target (AnatomicalTooth, DiagnosisDropRow, FindingRow). Disabled
  // when there's no active multi-selection — the multi-selection IS
  // the implicit "tokens" payload for the broad drop.
  const broadPaneAnatomicalRef = useMemo(() => ({
    kind: 'dental-tooth-surface',
    payload: { tokens: selectedTokens },
    summary: selectedTokens.length === 1
      ? `Tooth #${selectedTokens[0]}`
      : `${selectedTokens.length} selected sites`,
  }), [selectedTokens]);
  const { isOver: _broadIsOver, ...broadPaneDropProps } = useChartDropTarget({
    accepts: ['procedure'],
    anatomicalRef: broadPaneAnatomicalRef,
    onDrop: handleBroadProcedureDrop,
    disabled: selectedTokens.length === 0,
  });

  const isFullBleed = presentation === 'fullBleed';
  const isTabletPaired = presentation === 'tabletPaired';
  // Variant M — in portrait the rail no longer sits to the right of the
  // chart; it stacks BELOW it (chart fills a full-width top band). That makes
  // the chart pane wide-and-short (a landscape-shaped box), so the tall/narrow
  // portrait arch geometry would letterbox badly — use the default landscape
  // arch (geometry: null) in every paired branch now.
  const portraitArchGeometry = null;
  return (
    <div
      // Centered chart pane — replaces the legacy `fixed inset-0` full-
      // viewport overlay. Z_CHART_PANE sits BELOW Z_WIDGET_RAIL so the
      // right-edge widget rail stays reachable while the chart is open
      // (this is the load-bearing change that breaks the
      // "widgets blocked when chart open" pain point). The right offset
      // matches the rail's focused width (~420px) so the chart never
      // visually clashes with it. In `expanded` mode the pane grows to
      // most of the viewport but still respects the rail boundary on
      // the right.
      //
      // `presentation: 'fullBleed'` (legacy chairside-on-tablet) bypasses
      // the rail-reservation: chart covers the viewport at
      // Z_WIDGET_OVERLAY, intentionally hiding the rail underneath.
      //
      // `presentation: 'tabletPaired'` (Variant M) is the new chairside
      // default: chart sits left of the rail with a responsive right-gap
      // (phone bottom-sheet < md ≤ portrait-iPad 296 px ≤ landscape-iPad
      // 368 px). Z stays at Z_CHART_PANE so the rail above remains
      // clickable; the rail self-locks at 'focused' size by subscribing
      // to useTabletDentalChartStore. Both tablet branches are gated by
      // coarse-pointer + dentist upstream so desktop / non-dentists never
      // hit this mode.
      style={{ zIndex: isFullBleed ? Z_WIDGET_OVERLAY : Z_CHART_PANE }}
      className={[
        'arogya-chart-pane',
        'fixed overflow-y-auto bg-white dark:bg-slate-900 shadow-2xl',
        'ring-1 ring-slate-200 dark:ring-slate-700',
        isFullBleed
          ? 'inset-0 rounded-none'
          : isTabletPaired
            ? [
              'rounded-lg',
              // Chart fills a full-width top band, leaving the bottom 40 %
              // for the procedure rail. This is the layout on phones (< md)
              // AND on tablet PORTRAIT at every width — the rail stacks
              // below the chart there (see ProcedureWidget bottom-band).
              'top-14 left-2 right-2 bottom-[40%]',
              // Tablet LANDSCAPE (≥ md) only — chart left, rail right. The
              // right offset exactly equals the rail's display width (380)
              // so the chart pane's right edge meets the rail's left edge
              // with NO gap. DoctorQueueView's workflow panel underneath
              // doesn't pad for the rail, so any gap here lets workflow
              // labels (e.g. "QUEUE", "monitoring…") bleed through. The
              // rail's rounded-left corners that would otherwise expose
              // 24×24 triangles of workflow are squared off inside
              // ProcedureWidget when pairedFullSize. In portrait we stop
              // overriding the top-band geometry above so the chart stays
              // full-width with the rail stacked underneath.
              isPortrait
                ? ''
                : 'md:top-14 md:bottom-2 md:right-[380px]',
            ].join(' ')
            : [
              'rounded-lg',
              expanded
                ? 'top-2 left-2 right-[100px] bottom-2'
                : 'top-16 left-4 right-[420px] max-h-[calc(100vh-5rem)]',
            ].join(' '),
        'print:static print:inset-auto print:w-full print:h-auto',
        'print:bg-transparent print:p-0 print:shadow-none print:rounded-none print:ring-0',
      ].join(' ')}
      role="presentation"
      {...broadPaneDropProps}
    >
      {/* Variant M (tabletPaired) — chart-pane procedure picker as a
          centered modal portal. On desktop the picker still mounts in
          the side column (above); in tabletPaired the side column is
          hidden, so we portal the picker over the chart instead to keep
          the chart pane from ever resizing. The portal targets
          document.body so it escapes the chart pane's stacking context. */}
      {isTabletPaired && isChartPaneProcedureActive && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-900/50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            // Backdrop click — close the picker. Inner-content clicks
            // stopPropagation on the wrapper below so they don't trip
            // this handler.
            if (e.target === e.currentTarget) sdk.closeWidget();
          }}
        >
          <div
            className="w-[min(560px,calc(100vw-2rem))] max-h-[calc(100vh-4rem)] overflow-y-auto bg-white dark:bg-slate-900 rounded-xl shadow-2xl ring-1 ring-slate-200 dark:ring-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <ChartPaneProcedurePicker
              visit={visit}
              viewer={viewer}
              prefill={activeWidget?.context?.prefill || null}
              isDark={isDark}
              onClose={() => sdk.closeWidget()}
              onCreated={() => { refresh?.(); }}
            />
          </div>
        </div>,
        document.body,
      )}

      {/* Variant M — per-tooth Summary overlay. Mounts as an
          `absolute inset-0` cover over the entire chart pane (header
          included) when the dentist taps `📋 Summary` in the header.
          Wraps FindingsSummary so the underlying projection + row
          rendering stay single-sourced; surfaces its own pill row that
          drives FindingsSummary's now-controllable `statusFilter`. */}
      {isTabletPaired && summaryOpen && (
        <TabletChartSummaryOverlay
          isDark={isDark}
          teethWithFindingsCount={displayMarkers?.size ?? 0}
          counts={tabletSummary.counts}
          onBack={onSummaryToggle || (() => {})}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          renderFindings={({ statusFilter, setStatusFilter }) => (
            <div className="p-3">
              <FindingsSummary
                entriesByToken={entriesByToken}
                notesByToken={notesByToken}
                markers={displayMarkers}
                isDark={isDark}
                onPickTooth={(fdi) => handleToothClick(fdi)}
                selectionMode={selectionMode}
                patientId={patientId}
                onRefresh={refresh}
                addOptimisticSitting={addOptimisticSitting}
                onProcedureDrop={handleProcedureDrop}
                chartMode={chartMode}
                currentVisitId={currentVisitId}
                controlledStatusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
              />
            </div>
          )}
        />
      )}

      {/* Phase 7 — inline drop-flow toast. Positioned at the top of the
          chart pane (sticky so it stays visible even when the dentist
          scrolls long findings lists). Auto-dismisses; click to close. */}
      {paneToast && (
        <div
          className={[
            'sticky top-2 z-20 mx-auto w-[fit-content] max-w-[90%]',
            'px-3 py-1.5 text-xs font-medium rounded-md shadow-lg border',
            paneToast.tone === 'warn'
              ? 'bg-amber-50 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100 border-amber-400 dark:border-amber-600'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 border-slate-300 dark:border-slate-600',
          ].join(' ')}
          role="status"
          aria-live="polite"
          onClick={() => setPaneToast(null)}
        >
          {paneToast.text}
        </div>
      )}

      {/* Phase 7 — floating cursor chip. `pointer-events: none` so it
          never swallows the drop. Positioned at viewport coords +
          small offset so it stays just to the right/below the cursor. */}
      {dragHint && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: dragHint.y + 14,
            left: dragHint.x + 14,
            pointerEvents: 'none',
            zIndex: Z_TOAST,
          }}
          className={[
            'px-2.5 py-1 text-[11px] font-medium rounded-md shadow-lg whitespace-nowrap',
            dragHint.tone === 'warn'
              ? 'bg-amber-900 text-amber-50 border border-amber-700'
              : 'bg-slate-900 text-white border border-slate-700',
          ].join(' ')}
          aria-hidden="true"
        >
          {dragHint.text}
        </div>,
        document.body,
      )}

      <style>{`
        /* Phase 3 — drop-target highlight. AnatomicalTooth sets
           data-drop-active="true" via useChartDropTarget while a
           valid procedure payload hovers. Teal dashed ring + a soft
           filter glow draws the dentist's eye to the active tooth. */
        g[data-drop-active="true"] {
          filter: drop-shadow(0 0 3px rgba(20, 184, 166, 0.55));
        }
        g[data-drop-active="true"] .arogya-tooth-hit-rect {
          stroke: #0d9488;
          stroke-dasharray: 4 3;
          stroke-width: 1.5;
          fill: rgba(13, 148, 136, 0.06);
        }
        /* Phase 3 extension — summary rows (diagnosis rows in
           ToothActionsPanel; tooth rows in FindingsSummary). Both carry
           the .arogya-summary-drop-row class so this scoped rule
           highlights them without bleeding into other rows. */
        .arogya-summary-drop-row[data-drop-active="true"] {
          outline: 2px dashed #0d9488;
          outline-offset: 2px;
          background-color: rgba(13, 148, 136, 0.08);
          transition: outline-color 80ms linear, background-color 80ms linear;
        }
        /* Phase 6 — broad chart-pane drop zone. Subtle inset shadow so
           the dentist sees the whole pane is a drop target during drag
           (only fires when a multi-selection is active; useChartDropTarget
           toggles data-drop-active when its accepts list matches the
           dragged payload type AND disabled is false). Per-tooth /
           per-row child highlights remain the primary signal. */
        .arogya-chart-pane[data-drop-active="true"] {
          box-shadow: inset 0 0 0 2px rgba(20, 184, 166, 0.45),
                      0 24px 60px rgba(0,0,0,0.18);
        }
        /* Print path is handled by the dedicated popup window opened
           from the Print button (handlePrintChart). The native @media
           print rules below stay minimal — only used as a graceful
           fallback if the dentist hits Cmd+P from the page directly
           (popup-blocker scenarios). The popup writes its own self-
           contained stylesheet so dark-mode / Tailwind / surrounding
           app chrome never leak into the output. */
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Tooth chart"
        className={[
          'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-xl ring-1 ring-teal-200 dark:ring-teal-800',
          'print:shadow-none print:ring-0 print:mt-0 print:max-w-full',
          expanded
            ? 'w-full h-full max-w-none rounded-none p-4 m-0 flex flex-col min-h-0'
            : 'w-full max-w-5xl mt-8 rounded-lg p-5 md:flex md:flex-col md:min-h-0 md:max-h-[calc(100vh-7rem)]',
        ].join(' ')}
        data-tooth-chart-dialog
      >
        {/* Print-only letterhead — clinic name + phone (left), patient
            details + visit date + follow-up (right). Replaces the
            on-screen header on @media print. */}
        <div className="hidden print:block arogya-print-header border-b border-slate-300 pb-2 mb-3 text-slate-900">
          <div className="flex items-baseline justify-between gap-4">
            <div>
              {clinicBranding.name && (
                <div className="text-base font-bold leading-tight">{clinicBranding.name}</div>
              )}
              {clinicBranding.phone && (
                <div className="text-[10px] text-slate-700">{clinicBranding.phone}</div>
              )}
              {visit?.doctor_name && (
                <div className="text-[11px] text-slate-700 mt-0.5">Dr. {visit.doctor_name}</div>
              )}
            </div>
            <div className="text-right text-[11px] leading-snug">
              <div>
                <strong>{visit?.patient_name || 'Patient'}</strong>
                {visit?.patient_age != null && ` · ${visit.patient_age}y`}
              </div>
              {visit?.visit_date && (
                <div>Visit: {formatPrintDate(visit.visit_date)}</div>
              )}
              {visit?.follow_up_date && (
                <div className="mt-0.5">
                  <strong>Next visit:</strong> {formatPrintDate(visit.follow_up_date)}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="shrink-0 flex items-center justify-between gap-3 mb-3 flex-wrap print:hidden">
          <div>
            <h2 className="text-base font-semibold">🦷 Tooth chart</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {visit?.patient_name || 'Patient'}
              {visit?.patient_age != null && ` · ${visit.patient_age} y`}
              {' · click teeth to view history, add a note, or add a procedure'}
            </p>
          </div>
          <HeaderControls
            isDark={isDark}
            expanded={expanded}
            onToggleExpanded={() => setExpanded((v) => !v)}
            onClose={onClose}
            onPrint={handlePrintChart}
            selectionMode={selectionMode}
            setSelectionMode={setSelectionMode}
            surfaceMode={surfaceMode}
            setSurfaceMode={setSurfaceMode}
            setSelectedTokens={setSelectedTokens}
            dentitionMode={dentitionMode}
            setDentitionOverride={setDentitionOverride}
            autoOpenChart={autoOpenChart}
            setAutoOpenChart={setAutoOpenChart}
            presentation={presentation}
            summaryOpen={summaryOpen}
            onSummaryToggle={onSummaryToggle}
            summaryCount={displayMarkers?.size ?? 0}
          />
        </div>

        {/* F8.6 — workflow-hint banner. Shows for patients with NO
            existing findings on first open (per-patient localStorage
            marker). Dismisses on first tooth click or via the ✕. */}
        {!hintDismissed && displayMarkers.size === 0 && (
          <div className="shrink-0 mb-3 rounded-md border border-sky-300 dark:border-sky-700 bg-sky-50 dark:bg-sky-900/30 px-3 py-1.5 text-xs text-sky-800 dark:text-sky-200 flex items-center gap-2 flex-wrap print:hidden">
            <span aria-hidden="true">💡</span>
            <span>
              <strong>Start by clicking any tooth</strong> to add a
              diagnosis. Already diagnosed? Plan a procedure from the
              diagnosis row in the side panel.
            </span>
            <button
              type="button"
              onClick={dismissHint}
              className="ml-auto px-2 py-0.5 text-[11px] rounded-md border border-sky-400 dark:border-sky-600 bg-white dark:bg-slate-800 text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-900/40"
              aria-label="Dismiss hint"
            >
              Got it
            </button>
          </div>
        )}

        {displayMarkers.size === 0 && !panelOpen && (
          <div className="shrink-0 mb-2 text-xs italic text-slate-500 dark:text-slate-400">
            No dental history on file. Click any tooth to add a note or procedure.
          </div>
        )}

        <div className={[
          // Row fills remaining dialog space and never overflows it. The
          // dialog (flex-col + min-h-0) is the height-bounding parent;
          // each column scrolls internally via overflow-auto + min-h-0.
          // Mobile stack keeps natural growth and falls back on the
          // pane's outer max-h + overflow-y-auto.
          expanded
            ? 'flex gap-4 items-stretch min-h-0 flex-1'
            : 'flex flex-col md:flex-row md:items-stretch gap-4 md:min-h-0 md:flex-1',
        ].join(' ')}>
          {/* Chart column */}
          <div ref={archHostRef} className="flex-1 min-w-0 relative md:overflow-auto md:min-h-0">
            {/* Variant M — tablet tooth-action popover. Anchored to the
                primary selected tooth's bbox; replaces the side-column
                ToothActionsPanel so the chart pane never resizes on a
                tooth-tap. Tablet-paired mode only. */}
            {isTabletPaired && selectedTokens.length > 0 && !isChartPaneProcedureActive && (
              <TabletToothActionPopover
                visit={visit}
                viewer={viewer}
                selectedTokens={selectedTokens}
                notesByToken={notesByToken}
                displayMarkers={displayMarkers}
                isDark={isDark}
                archHostRef={archHostRef}
                onClose={() => setSelectedTokens([])}
              />
            )}
            <Suspense fallback={<OdontogramSkeleton isDark={isDark} />}>
              <Odontogram
                dentitionMode={dentitionMode}
                markers={displayMarkers}
                selectedTeeth={selectedTokens.length > 0 ? selectedTokens : pendingTokens}
                onToothClick={handleToothClick}
                onQuadrantClick={handleQuadrantClick}
                onFmClick={handleFmClick}
                onQuadrantFocusChange={setFocusedQuadrants}
                activeQuadrants={activeQuadrants}
                fmActive={fmActive}
                quadrantStatusByQ={quadrantStatusByQ}
                fmStatus={fmStatus}
                statusFilter={statusFilter}
                fdiStatusSet={fdiStatusSet}
                isDark={isDark}
                onProcedureDrop={handleProcedureDrop}
                geometry={portraitArchGeometry}
              />
            </Suspense>

            {/* Multi-select chip bar. When fmActive (FM token OR all four
                Q's selected), collapse the FM-causing tokens into a single
                "Full mouth ✕" chip so the bar reads consistently with the
                rest of the chart UI ("Full mouth", not "Q1, Q2, Q3, Q4").
                Any non-FM/non-Q tokens (e.g. a specific tooth) still
                render as individual chips. */}
            {selectionMode === 'multi' && selectedTokens.length > 0 && (() => {
              const isFmToken = (t) => {
                const s = typeof t === 'string' && t.startsWith('gum:') ? t.slice(4) : t;
                return s === 'FM' || /^Q[1-4]$/.test(s);
              };
              const fmExtras = fmActive ? selectedTokens.filter((t) => !isFmToken(t)) : [];
              const visibleCount = fmActive ? 1 + fmExtras.length : selectedTokens.length;
              const removeFmScope = () => setSelectedTokens(
                (prev) => prev.filter((t) => !isFmToken(t)),
              );
              return (
                <div className="mt-2 flex items-center gap-1.5 flex-wrap rounded-md border border-teal-400 bg-teal-50 dark:bg-teal-900/30 dark:border-teal-700 px-2 py-1.5">
                  <span className="text-[11px] font-semibold mr-1">
                    {visibleCount} selected:
                  </span>
                  {fmActive ? (
                    <button
                      type="button"
                      onClick={removeFmScope}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-teal-400 dark:border-teal-700 bg-white dark:bg-slate-800 text-[10px] hover:bg-teal-100 dark:hover:bg-slate-700"
                      title="Remove full-mouth scope"
                    >
                      Full mouth
                      <span className="opacity-50">✕</span>
                    </button>
                  ) : null}
                  {(fmActive ? fmExtras : selectedTokens).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => removeOneToken(t)}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-teal-400 dark:border-teal-700 bg-white dark:bg-slate-800 text-[10px] hover:bg-teal-100 dark:hover:bg-slate-700"
                      title="Remove from selection"
                    >
                      {t.replace('gum:', '🩸')}
                      <span className="opacity-50">✕</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="ml-auto px-2 py-0.5 text-[10px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    Clear
                  </button>
                </div>
              );
            })()}

            {/* Status-dot legend. Diagnosis colors are solid (categorical)
                while procedure-lifecycle dots use the bottom-up half-fill
                so the dentist can read a tooth's progression at a glance. */}
            <div className="mt-3 flex items-center gap-x-3 gap-y-1 flex-wrap text-[10px] text-slate-600 dark:text-slate-300">
              {/* Diagnosis severity — solid bubbles. */}
              {[
                { fill: '#dc2626', label: 'Severe' },
                { fill: '#ea580c', label: 'Diagnosis' },
                { fill: '#ca8a04', label: 'Watch' },
              ].map((s) => (
                <span key={s.label} className="inline-flex items-center gap-1">
                  <span
                    aria-hidden="true"
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      background: s.fill,
                      border: '1px solid rgba(15,23,42,0.18)',
                      display: 'inline-block',
                    }}
                  />
                  {s.label}
                </span>
              ))}
              {/* Procedure progression — half-fill examples. */}
              <span className="inline-flex items-center gap-2 ml-1">
                {[
                  { status: 'planned',     label: 'Planned',     color: '#2563eb' },
                  { status: 'in_progress', label: 'In progress', color: '#0d9488' },
                  { status: 'completed',   label: 'Completed',   color: '#16a34a' },
                ].map((p, i) => {
                  // Heights mirror STATUS_FILL levels (0.25 / 0.70 / 1.00)
                  // so the legend is a literal sample of what the chart
                  // draws — no separate maths, no drift.
                  const level = i === 0 ? 0.25 : i === 1 ? 0.70 : 1.0;
                  return (
                    <span
                      key={p.label}
                      className="inline-flex items-center gap-1"
                      title={`Procedure ${p.label.toLowerCase()}`}
                    >
                      <svg
                        width={11}
                        height={11}
                        viewBox="-6 -6 12 12"
                        aria-hidden="true"
                      >
                        <circle r={5} fill="#16a34a" fillOpacity={0.15} />
                        <clipPath id={`legend-clip-${i}`}>
                          <rect x={-5} y={5 - (10 * level)} width={10} height={10 * level} />
                        </clipPath>
                        <circle r={5} fill={p.color} clipPath={`url(#legend-clip-${i})`} />
                        <circle r={5} fill="none" stroke="rgba(15,23,42,0.45)" strokeWidth={0.6} />
                      </svg>
                      {p.label}
                    </span>
                  );
                })}
              </span>
            </div>

            {/* Procedure-history letter legend — secondary read. */}
            <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] text-slate-500 dark:text-slate-400">
              <span><strong>R</strong> RCT</span>
              <span><strong>C</strong> Crown</span>
              <span><strong>F</strong> Filled</span>
              <span><strong>M</strong> Missing</span>
              <span><strong>B</strong> Bridge</span>
              <span><strong>X</strong> Fractured</span>
              <span><strong>I</strong> Impacted</span>
              <span><strong>Im</strong> Implant</span>
              <span><strong>📝</strong> Note</span>
              <span><strong>🩸</strong> Gum</span>
            </div>
          </div>

          {/* F6.6 — action panel column is ALWAYS rendered (compact OR
              expanded). In expanded mode it sits next to the chart at
              the same flex level and scrolls internally so the page
              itself never gains a scrollbar.
              Variant M — in `tabletPaired` mode the per-tooth summary
              moves to the `📋 Summary` overlay; the side column collapses
              when nothing tooth-driven is active so the arch gets the
              full chart-pane width. */}
          {/* Side column — desktop only. tabletPaired never resizes the
              chart: tooth-action UI is the floating TabletToothActionPopover
              above; chart-pane procedure picker renders as a centered
              modal portal further down. */}
          {!isTabletPaired && (
            <div className={[
              'shrink-0 print:hidden md:overflow-y-auto md:min-h-0',
              expanded
                ? 'w-80 lg:w-96'
                : 'md:w-80 lg:w-96',
            ].join(' ')}>
              {isChartPaneProcedureActive ? (
                /* Phase 2.C — chart-pane inline procedure picker. Replaces
                   the side panel content while the picker is mounted. On
                   save or cancel, closeWidget() clears the SDK store →
                   effect re-evaluates → ToothActionsPanel returns. */
                <ChartPaneProcedurePicker
                  visit={visit}
                  viewer={viewer}
                  prefill={activeWidget?.context?.prefill || null}
                  isDark={isDark}
                  onClose={() => sdk.closeWidget()}
                  onCreated={() => { refresh?.(); }}
                />
              ) : panelOpen ? (
                <ToothActionsPanel
                  tokens={selectedTokens}
                  visit={visit}
                  viewer={viewer}
                  procedureEntries={procedureEntries}
                  noteEntries={noteEntries}
                  onAdded={() => { refresh?.(); }}
                  onOptimisticAdd={(entry) => {
                    addOptimisticEntry?.(entry);
                    if (selectionMode === 'multi') clearSelection();
                  }}
                  onClose={() => setSelectedTokens([])}
                  isDark={isDark}
                />
              ) : (
                <FindingsSummary
                  entriesByToken={entriesByToken}
                  notesByToken={notesByToken}
                  markers={displayMarkers}
                  isDark={isDark}
                  onPickTooth={(fdi) => handleToothClick(fdi)}
                  selectionMode={selectionMode}
                  patientId={patientId}
                  onRefresh={refresh}
                  addOptimisticSitting={addOptimisticSitting}
                  onProcedureDrop={handleProcedureDrop}
                  currentVisitId={currentVisitId}
                  // G — controlled by the lifted filter so the chart reacts too,
                  // but desktop still shows its own pill row.
                  controlledStatusFilter={statusFilter}
                  onStatusFilterChange={setStatusFilter}
                  showStatusBanner
                />
              )}
            </div>
          )}
        </div>

        {/* ──────────────────────────────────────────────────────────
            Print-only block: legend + clinical summary.

            Renders below the chart (DOM order) on @media print. Patient,
            graduate clinician, and handoff doctor all see the same
            sheet — no payment info, no internal clinician notes,
            severity labels intentionally dropped (alarming to patients,
            still visible on screen for the dentist).
            ────────────────────────────────────────────────────────── */}
        <div className="hidden print:block mt-4 text-xs arogya-print-summary">
          {/* Marker legend — first thing a grad / handoff doctor needs
              to make sense of the chart they're holding. */}
          <div className="border-t border-slate-300 pt-2 text-[10px]">
            <div className="font-semibold mb-1">Legend</div>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <LegendItem kind="rct" label="Root canal" />
              <LegendItem kind="crown" label="Crown" />
              <LegendItem kind="bridge" label="Bridge" />
              <LegendItem kind="filled" label="Filling" />
              <LegendItem kind="missing" label="Missing / extracted" />
              <LegendItem kind="fractured" label="Fractured" />
              <LegendItem kind="impacted" label="Impacted" />
              <LegendItem kind="gum" label="Gum / periodontal" />
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-slate-700">
              <span>○ planned · ● done</span>
              <span>· Tooth numbering: FDI (ISO 3950)</span>
            </div>
          </div>

          <h3 className="text-sm font-semibold mt-3 mb-2 arogya-print-section-title">Summary</h3>
          <div className="space-y-2">
            {(diagnosesByVisit?.get(visitId) || []).length > 0 && (
              <div>
                <div className="font-semibold arogya-print-section-title">Diagnoses ({diagnosisCount})</div>
                <ul className="list-disc ml-5">
                  {(diagnosesByVisit?.get(visitId) || []).map((d) => (
                    <li key={d.meta_id}>
                      {d.content}
                      {d.tooth_numbers?.length
                        ? ` — ${d.tooth_numbers.map((t) => tokenToHumanLabel(t, { compact: true })).join(', ')}`
                        : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {plannedProcedures.length > 0 && (
              <div>
                <div className="font-semibold arogya-print-section-title">Planned procedures ({plannedProcedures.length})</div>
                <ul className="list-disc ml-5">
                  {plannedProcedures.map((s) => {
                    const diagContent = s.linked_diagnosis_metadata_id
                      ? diagnosisContentByMetaId.get(s.linked_diagnosis_metadata_id)
                      : null;
                    return (
                      <li key={s.sitting_id}>
                        {s.procedure_name || 'Procedure'}
                        {s.tooth_numbers?.length
                          ? ` — ${s.tooth_numbers.map((t) => tokenToHumanLabel(t, { compact: true })).join(', ')}`
                          : ''}
                        {diagContent ? ` · for: ${diagContent}` : ''}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {completedProcedures.length > 0 && (
              <div>
                <div className="font-semibold arogya-print-section-title">Past treatments ({completedProcedures.length})</div>
                <ul className="list-disc ml-5">
                  {completedProcedures.map((s) => (
                    <li key={s.sitting_id}>
                      {s.procedure_name || 'Procedure'}
                      {s.tooth_numbers?.length
                        ? ` — ${s.tooth_numbers.map((t) => tokenToHumanLabel(t, { compact: true })).join(', ')}`
                        : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * FindingsSummary — the side-panel summary the dentist sees when no
 * teeth are selected. Re-designed for dentist-language scanning:
 *
 *   1. A three-pill status banner at the top (Open / In progress / Done)
 *      — click a pill to filter the list to that bucket.
 *   2. Sections grouped by SCOPE (Full mouth → Quadrants → Teeth →
 *      Surfaces → Gum) so quadrant-wide and full-mouth findings are
 *      first-class citizens rather than hidden inside the per-tooth list.
 *   3. Each row reads as PROBLEM → PLAN → STATUS so the dentist scans
 *      "what's wrong / what we're doing / where it is" in one glance.
 *
 * Underlying data shape is unchanged — we still read `markers`,
 * `entriesByToken`, `notesByToken` from `usePatientToothHistory`. Only
 * the projection + rendering is new.
 */
function FindingsSummary({
  entriesByToken, notesByToken, markers, isDark, onPickTooth, selectionMode,
  // Procedure-action wiring — H2/H4. Optional; if absent the toolbar
  // simply doesn't render (legacy callers / non-dentist contexts).
  patientId = null,
  onRefresh = null,
  addOptimisticSitting = null,
  // Phase 3 — Drop a procedure chip onto a finding row here to open
  // the procedure picker pre-bound to that token. Optional; absent in
  // non-dentist contexts.
  onProcedureDrop = null,
  // C6 — summary mirrors the chart's mode. In Recent ('fresh') we hide
  // pure-history rows (matches the chart's filtered marker set), sort
  // newest-first, and mute old completed rows.
  chartMode = 'full',
  currentVisitId = null,
  // Variant M — TabletChartSummaryOverlay provides its own filter pill
  // row, so we let it drive the status filter as a controlled value.
  // When `controlledStatusFilter` is supplied, the internal banner is
  // suppressed (the overlay already shows pills) and state changes flow
  // back via `onStatusFilterChange`. Both undefined → legacy uncontrolled
  // behaviour with the inline banner.
  controlledStatusFilter,
  onStatusFilterChange,
  // G — render the inline StatusBanner pills even when the filter value is
  // controlled (desktop drives the lifted filter but still shows its own pills).
  // Defaults to the legacy behaviour (pills only when uncontrolled) so the
  // tablet overlay — which renders its OWN pills — stays single-row.
  showStatusBanner,
}) {
  const isControlled = typeof controlledStatusFilter === 'string'
    && typeof onStatusFilterChange === 'function';
  const [internalStatusFilter, setInternalStatusFilter] = React.useState('all');
  const statusFilter = isControlled ? controlledStatusFilter : internalStatusFilter;
  const setStatusFilter = isControlled ? onStatusFilterChange : setInternalStatusFilter;
  const showBanner = showStatusBanner != null ? showStatusBanner : !isControlled;
  // H5 — currently-viewed procedure in the popover. null when closed.
  const [viewingPlans, setViewingPlans] = React.useState(null);
  const [viewingTokenLabel, setViewingTokenLabel] = React.useState('');

  // Sections + status counts come from the single shared `buildSummary`
  // selector (summaryProjection.js) so the desktop StatusBanner and the
  // mobile TabletChartSummaryOverlay can never show different numbers.
  // It folds in the Recent-mode (`fresh`) current-visit filter + recount.
  const { sections, counts, totalItems } = React.useMemo(() => buildSummary({
    entriesByToken, notesByToken, markers, chartMode, currentVisitId,
  }), [entriesByToken, notesByToken, markers, chartMode, currentVisitId]);

  const cardClass = [
    'rounded-md border p-3',
    isDark ? 'bg-slate-900 border-slate-700 text-slate-200'
           : 'bg-white border-slate-300 text-slate-800',
  ].join(' ');

  if (totalItems === 0) {
    return (
      <div className={[cardClass, 'text-xs italic'].join(' ')}>
        No dental findings on record yet. Click any tooth on the chart
        to add the first one.
      </div>
    );
  }

  return (
    <div className={cardClass}>
      {/* Show the inline pills unless an outer surface (TabletChartSummaryOverlay)
          provides its own — avoids two stacked pill rows. Desktop passes
          showStatusBanner=true so its pills stay even though the filter value is
          controlled (lifted) for the chart. */}
      {showBanner && (
        <StatusBanner
          counts={counts}
          activeFilter={statusFilter}
          onChange={setStatusFilter}
          isDark={isDark}
        />
      )}

      <div className={['space-y-3 pr-1', showBanner ? 'mt-3' : ''].join(' ')}>
        {sections.map((section) => (
          <ScopeSection
            key={section.scope}
            section={section}
            statusFilter={statusFilter}
            isDark={isDark}
            onPickToken={onPickTooth}
            // H2 — pass-through so each FindingRow can render the
            // procedure action toolbar.
            patientId={patientId}
            onRefresh={onRefresh}
            addOptimisticSitting={addOptimisticSitting}
            onViewProcedure={(plansList, tokenLabel) => {
              setViewingPlans(plansList);
              setViewingTokenLabel(tokenLabel);
            }}
            onProcedureDrop={onProcedureDrop}
            currentVisitId={currentVisitId}
          />
        ))}
      </div>

      <div className={[
        'mt-2 text-[11px] italic',
        isDark ? 'text-slate-400' : 'text-slate-500',
      ].join(' ')}>
        {selectionMode === 'multi'
          ? 'Tap a row to pick that tooth/region for a new diagnosis or procedure.'
          : 'Tap a row to view that tooth/region’s history.'}
      </div>

      {viewingPlans && viewingPlans.length > 0 && (
        <ProcedureViewPopover
          plans={viewingPlans}
          tokenLabel={viewingTokenLabel}
          onClose={() => setViewingPlans(null)}
          isDark={isDark}
        />
      )}
    </div>
  );
}

// Projection logic lives in `./summaryProjection`. It's imported at the
// top of this file so we can keep the dialog focused on rendering. See
// summaryProjection.js for the PROBLEM/PLAN/STATUS rules — especially
// the auto-generated-note filter (bug 2) and the procedure-row fallback
// for the PLAN line (bug 3).

// ── Status banner (3 filter pills) ────────────────────────────────

function StatusBanner({ counts, activeFilter, onChange, isDark }) {
  // Buckets come from the shared STATUS_PILLS list so desktop and the
  // mobile overlay always expose the identical set of filters.
  const pills = STATUS_PILLS.map((id) => ({ id, count: counts[id] || 0 }));
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button
        type="button"
        onClick={() => onChange('all')}
        className={[
          'text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-md border',
          activeFilter === 'all'
            ? (isDark
              ? 'bg-slate-700 text-slate-100 border-slate-500'
              : 'bg-slate-200 text-slate-900 border-slate-400')
            : (isDark
              ? 'bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700'
              : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'),
        ].join(' ')}
        aria-pressed={activeFilter === 'all'}
      >
        All · {counts.all || 0}
      </button>
      {pills.map(({ id, count }) => {
        const meta = STATUS_META[id];
        const isActive = activeFilter === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(isActive ? 'all' : id)}
            className={[
              'text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-md border inline-flex items-center gap-1',
              isActive ? `${meta.classes.light} ${meta.classes.dark}` : (
                isDark
                  ? 'bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700'
                  : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'
              ),
            ].join(' ')}
            aria-pressed={isActive}
            title={meta.description}
          >
            <span aria-hidden="true">{meta.dot}</span>
            <span>{meta.label}</span>
            <span className="opacity-80">· {count}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Scope section + finding row ───────────────────────────────────

function ScopeSection({
  section, statusFilter, isDark, onPickToken,
  // H2 — passed through to every FindingRow so it can render the
  // procedure-action toolbar. All are optional; legacy callers omit
  // them and the toolbar quietly omits itself.
  patientId = null, onRefresh = null, addOptimisticSitting = null,
  onViewProcedure = null,
  onProcedureDrop = null,
  chartMode = 'full',
  currentVisitId = null,
}) {
  const visible = statusFilter === 'all'
    ? section.items
    : section.items.filter((it) => it.status === statusFilter);
  if (visible.length === 0) return null;
  return (
    <div>
      <div className={[
        'text-[10px] font-semibold uppercase tracking-wide flex items-center gap-1.5 mb-1.5',
        isDark ? 'text-slate-400' : 'text-slate-500',
      ].join(' ')}>
        <span aria-hidden="true">{section.meta.icon}</span>
        <span>{section.meta.label}</span>
        <span className="opacity-70">· {visible.length}</span>
      </div>
      <div className="space-y-1.5">
        {visible.map((item) => (
          <FindingRow
            key={item.token}
            item={item}
            isDark={isDark}
            patientId={patientId}
            onRefresh={onRefresh}
            addOptimisticSitting={addOptimisticSitting}
            onViewProcedure={onViewProcedure}
            onPick={() => onPickToken && onPickToken(item.token)}
            onProcedureDrop={onProcedureDrop}
            chartMode={chartMode}
            currentVisitId={currentVisitId}
          />
        ))}
      </div>
    </div>
  );
}

function FindingRow({
  item, isDark, onPick,
  // H2 — procedure-action toolbar wiring. All optional.
  patientId = null, onRefresh = null, addOptimisticSitting = null,
  onViewProcedure = null,
  // Phase 3 — drop target callback. Called by useChartDropTarget when a
  // procedure payload is dropped on this finding row. Receives
  // (payload, fdi/token) so the container can compose the prefill.
  onProcedureDrop = null,
  chartMode = 'full',
  currentVisitId = null,
}) {
  const statusMeta = STATUS_META[item.status] || STATUS_META.open;
  // Mute completed historical rows in Recent mode so the dentist's eye
  // lands on what's active in THIS visit. Soft signal — opacity 60 +
  // light grayscale — restored on hover for inspection. Audited in
  // both light + dark surfaces per the day-mode contrast rule.
  // One all-history chart — no greying of older "done elsewhere" work.
  const rowBase = [
    'w-full text-left rounded-md border px-2.5 py-2 transition',
    isDark
      ? 'bg-slate-800 border-slate-700 hover:bg-slate-700'
      : 'bg-slate-50 border-slate-300 hover:bg-slate-100',
  ].join(' ');

  // The card is a clickable region but we use role="button" instead of
  // a real <button> element so we can nest the action toolbar's
  // <button>s as DOM children without invalid markup (button-in-button).
  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onPick?.();
    }
  };

  // Phase 3 — Procedure drag-drop. Each summary row is a drop target
  // for `'procedure'` payloads. Token is the row's anatomical token
  // (FDI / Q*/FM); the container's onProcedureDrop callback runs the
  // dental-specific lookup + opens the picker.
  const { isOver: _isOver, ...dropProps } = useChartDropTarget({
    accepts: ['procedure'],
    anatomicalRef: {
      kind: 'dental-tooth-surface',
      payload: { tokens: [String(item.token)] },
      summary: item.label || `Token ${item.token}`,
    },
    onDrop: (payload) => {
      if (typeof onProcedureDrop === 'function') {
        onProcedureDrop(payload, item.token);
      }
    },
    disabled: !onProcedureDrop,
  });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        // Suppress row select when the click came from inside the action
        // toolbar (which renders below). The toolbar's own handlers call
        // stopPropagation, so this is just defence-in-depth.
        if (e.defaultPrevented) return;
        onPick?.();
      }}
      onKeyDown={handleKey}
      className={[
        rowBase,
        'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500',
        'arogya-summary-drop-row',
      ].join(' ')}
      {...dropProps}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold leading-tight">
          {item.label}
        </span>
        <span
          className={[
            'text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border shrink-0 inline-flex items-center gap-1',
            statusMeta.classes.light, statusMeta.classes.dark,
          ].join(' ')}
        >
          <span aria-hidden="true">{statusMeta.dot}</span>
          <span>{statusMeta.label}</span>
        </span>
      </div>

      <dl className="mt-1.5 text-[11px] leading-snug space-y-0.5">
        <div className="flex gap-2">
          <dt className={[
            'shrink-0 font-semibold uppercase tracking-wide w-16',
            isDark ? 'text-slate-400' : 'text-slate-500',
          ].join(' ')}>Diagnosis</dt>
          <dd
            className={[
              'flex-1 flex items-start justify-between gap-2',
              item.problemTone === 'missing-diagnosis'
                ? (isDark ? 'text-amber-300 font-medium' : 'text-amber-700 font-medium')
                : (isDark ? 'text-slate-100' : 'text-slate-800'),
            ].join(' ')}
          >
            <span className="min-w-0">
              {item.problem}
              {item.problemSeverity && (
                <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  {' '}({item.problemSeverity})
                </span>
              )}
              {item.diagnosisCount + item.noteCount > 1 && (
                <span className={[
                  'italic ml-1',
                  isDark ? 'text-slate-400' : 'text-slate-500',
                ].join(' ')}>
                  +{(item.diagnosisCount + item.noteCount) - 1} more
                </span>
              )}
            </span>
            {/* GB — explicit action button when a diagnosis is missing.
                The whole row is clickable, but dentists shouldn't have
                to discover that — give them a real button labelled
                "+ Add" that says exactly what will happen. Stops event
                propagation so it doesn't double-fire the row click. */}
            {item.problemTone === 'missing-diagnosis' && (
              <button
                type="button"
                data-testid={`add-diagnosis-btn-${item.token}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onPick) onPick();
                  try {
                    window.dispatchEvent(new CustomEvent(
                      'arogya:focus-diagnosis-input',
                    ));
                  } catch (_e) { /* SSR / old browsers */ }
                }}
                className={[
                  'shrink-0 px-2 py-1 rounded-md border text-[10px] font-semibold uppercase tracking-wide',
                  'min-h-[28px]',
                  isDark
                    ? 'border-amber-500 bg-amber-900/40 text-amber-100 hover:bg-amber-900/60'
                    : 'border-amber-500 bg-amber-100 text-amber-800 hover:bg-amber-200',
                ].join(' ')}
                aria-label={`Add diagnosis on ${item.label}`}
              >
                + Add
              </button>
            )}
          </dd>
        </div>

        <div className="flex gap-2">
          <dt className={[
            'shrink-0 font-semibold uppercase tracking-wide w-16',
            isDark ? 'text-slate-400' : 'text-slate-500',
          ].join(' ')}>Plan</dt>
          <dd className={isDark ? 'text-slate-100' : 'text-slate-800'}>
            {item.plan ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewProcedure?.(
                    item.plans || (item.plan ? [item.plan] : []), item.label,
                  );
                }}
                className="inline-flex items-center gap-1 flex-wrap text-left hover:underline focus:underline cursor-pointer"
                title="View all procedures on this tooth"
                aria-label={`View all procedures on ${item.label}`}
              >
                <span className="font-medium">{item.plan.name}</span>
                <span aria-hidden="true">·</span>
                {/* Status dot matches the chart's half-fill palette so
                    "this row's tooth color = this row's status" reads
                    consistently across the chart and the summary. */}
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: statusFillColor(item.plan.status) }}
                  aria-hidden="true"
                />
                <span>{humanSittingStatus(item.plan.status)}</span>
                {item.plan.assignee && (
                  <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                    {' · '}{item.plan.assignee}
                  </span>
                )}
                {/* E — proposing doctor for suggested/planned work. */}
                {item.plan.originDoctor && (
                  <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                    {' · proposed by '}{item.plan.originDoctor}
                  </span>
                )}
                {/* Expiry — show the validity date on planned/awaiting rows, and
                    a rose "Expired" tag once past. Standing orders show their
                    review date instead. */}
                {item.status === 'planned' && (item.plan.expiresAt || item.plan.standing) && (() => {
                  const exp = item.plan.expiresAt
                    ? String(item.plan.expiresAt).slice(0, 10) : null;
                  const isExpired = exp && exp < new Date().toISOString().slice(0, 10);
                  const pretty = exp
                    ? new Date(exp).toLocaleDateString('en-IN', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })
                    : null;
                  const label = item.plan.standing
                    ? (pretty ? `review ${pretty}` : 'standing · no expiry')
                    : `valid till ${pretty}`;
                  return (
                    <>
                      <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                        {' · '}{label}
                      </span>
                      {isExpired && (
                        <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-100 text-rose-700 border border-rose-300 dark:bg-rose-900/40 dark:text-rose-200 dark:border-rose-700">
                          Expired
                        </span>
                      )}
                    </>
                  );
                })()}
                {/* Who cancelled (audit) — on cancelled rows. */}
                {item.plan.status === 'cancelled' && item.plan.cancelledBy && (
                  <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                    {' · cancelled by '}{item.plan.cancelledBy}
                  </span>
                )}
                {/* GC — "started Xm ago" only when the procedure is
                    actively in progress. Past procedures don't need
                    the timestamp here; their completed_at is implicit. */}
                {item.plan.status === 'in_progress' && item.plan.startedAt && (
                  <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                    {' · started '}{formatRelative(item.plan.startedAt)}
                  </span>
                )}
                {/* GC — cost context when the sitting carries one. */}
                {item.plan.finalCost != null && item.plan.finalCost > 0 && (
                  <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                    {' · ₹'}
                    {Number(item.plan.finalCost).toLocaleString('en-IN')}
                  </span>
                )}
                {item.extraPlanCount > 0 && (
                  <span className={[
                    'italic underline',
                    isDark ? 'text-sky-300' : 'text-sky-600',
                  ].join(' ')}>
                    +{item.extraPlanCount} more
                  </span>
                )}
              </button>
            ) : (
              <span className={[
                'italic',
                isDark ? 'text-slate-400' : 'text-slate-500',
              ].join(' ')}>
                Not planned yet
              </span>
            )}
          </dd>
        </div>
      </dl>

      {/* H2 — procedure-action toolbar. Renders only when a live plan
          exists on the row + the wiring props were passed through. The
          buttons are status-context-aware; the toolbar itself decides
          which CTA is primary based on item.plan.status. */}
      {item.status !== 'cancelled'
        && item.plan && item.plan.sittingId && patientId && onRefresh && (
        <div className="mt-2">
          <RowActionToolbar
            plan={item.plan}
            patientId={patientId}
            addOptimisticSitting={addOptimisticSitting}
            onRefresh={onRefresh}
            isDark={isDark}
          />
        </div>
      )}

      {item.updatedAt && (
        <div
          className={[
            'mt-1.5 text-[10px] text-right',
            isDark ? 'text-slate-400' : 'text-slate-500',
          ].join(' ')}
        >
          updated {formatRelative(item.updatedAt)}
        </div>
      )}
    </div>
  );
}


/**
 * 4-quadrant skeleton — renders while the Odontogram lazy chunk loads
 * (typical first-open ~150-300ms over the network). Gives the user
 * an instant sense of structure instead of a "Loading chart…" line.
 */
function OdontogramSkeleton({ isDark = false }) {
  const cell = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)';
  const border = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.12)';
  const Quadrant = () => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 1fr)',
        gap: 4,
        padding: 8,
        background: cell,
        border: `1px solid ${border}`,
        borderRadius: 6,
      }}
    >
      {Array.from({ length: 8 }, (_, i) => (
        <div
          key={i}
          style={{
            height: 28,
            background: cell,
            border: `1px solid ${border}`,
            borderRadius: 4,
            animation: 'tcs-shimmer 1.2s ease-in-out infinite',
            backgroundImage: isDark
              ? 'linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.04) 100%)'
              : 'linear-gradient(90deg, rgba(15,23,42,0.04) 0%, rgba(15,23,42,0.12) 50%, rgba(15,23,42,0.04) 100%)',
            backgroundSize: '200% 100%',
          }}
        />
      ))}
    </div>
  );
  return (
    <div
      role="status"
      aria-label="Loading tooth chart"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 6,
        padding: '0.5rem 0',
      }}
    >
      <Quadrant />
      <Quadrant />
      <Quadrant />
      <Quadrant />
    </div>
  );
}


// Inject the shimmer keyframe once per app load — matches the pattern
// DailyCollectionSummary uses; CRA is CSS-in-JS-less so this is the
// lightest path. Idempotent: only mounts if the <style> isn't already
// present.
if (typeof document !== 'undefined' && !document.getElementById('tcs-shimmer-kf')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'tcs-shimmer-kf';
  styleEl.textContent = (
    '@keyframes tcs-shimmer {'
    + ' 0% { background-position: 200% 0; }'
    + ' 100% { background-position: -200% 0; }'
    + '}'
  );
  document.head.appendChild(styleEl);
}


// ──────────────────────────────────────────────────────────────────
// HeaderControls — splits the header into an "always visible" group
// and a "secondary" group. At lg+ the secondary group sits inline
// alongside the primary buttons (same look as before). Below lg the
// secondary group collapses behind a ⋮ More dropdown so the header
// stops overflowing into 2-3 rows on tablets.
//
// All controls are read-only props; rendering them in both the inline
// and dropdown locations is safe because none of them hold local state.
// ──────────────────────────────────────────────────────────────────

function HeaderControls(props) {
  const {
    isDark, expanded, onToggleExpanded, onClose, onPrint,
    selectionMode, setSelectionMode,
    surfaceMode, setSurfaceMode, setSelectedTokens,
    dentitionMode, setDentitionOverride,
    autoOpenChart, setAutoOpenChart,
    // Variant M — Summary button + rose close pill render only when the
    // chart is mounted in `tabletPaired` presentation. Desktop / legacy
    // `fullBleed` / `pane` keep the existing small ✕.
    presentation = 'pane',
    summaryOpen = false,
    onSummaryToggle = null,
    summaryCount = 0,
  } = props;
  const isTabletPaired = presentation === 'tabletPaired';

  // Secondary controls — extracted into a render fn so we can mount
  // them twice (inline + inside the overflow menu) without state drift.
  const renderSecondary = () => (
    <>
      <div
        role="group"
        aria-label="Selection mode"
        className="inline-flex rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden"
      >
        {[
          { id: 'single', label: 'Single' },
          { id: 'multi', label: 'Multi' },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setSelectionMode(opt.id)}
            className={[
              'px-2 py-1 text-xs',
              selectionMode === opt.id
                ? 'bg-teal-600 text-white'
                : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700',
            ].join(' ')}
            title={opt.id === 'multi' ? 'Click multiple teeth then bulk-add' : 'Click one tooth at a time'}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div
        role="group"
        aria-label="Surface mode"
        className="inline-flex rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden"
      >
        {[
          { id: 'tooth', label: '🦷 Tooth' },
          { id: 'gum', label: '🩸 Gum' },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => {
              setSurfaceMode(opt.id);
              setSelectedTokens([]);
            }}
            className={[
              'px-2 py-1 text-xs',
              surfaceMode === opt.id
                ? (opt.id === 'gum' ? 'bg-rose-600 text-white' : 'bg-teal-600 text-white')
                : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700',
            ].join(' ')}
            title={opt.id === 'gum' ? 'Mark periodontal/gum findings' : 'Mark tooth procedures + notes'}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <select
        value={dentitionMode}
        onChange={(e) => setDentitionOverride(e.target.value)}
        className="px-2 py-1 text-xs rounded-md border bg-white dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600"
        aria-label="Dentition mode"
      >
        <option value="adult">Adult</option>
        <option value="mixed">Mixed</option>
        <option value="pediatric">Pediatric</option>
      </select>

      <button
        type="button"
        onClick={() => {
          const next = !autoOpenChart;
          setAutoOpenChart(next);
          writeAutoOpenPref(next);
        }}
        className={[
          'px-2 py-1 text-[11px] rounded-md border font-medium',
          autoOpenChart
            ? 'border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-200 hover:bg-teal-100 dark:hover:bg-teal-900/60'
            : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700',
        ].join(' ')}
        title={autoOpenChart
          ? 'Chart opens automatically when starting a consultation. Click to disable.'
          : 'Chart will NOT auto-open. Click to re-enable.'}
      >
        🔄 Auto-open: {autoOpenChart ? 'ON' : 'OFF'}
      </button>
    </>
  );

  return (
    <div
      className={[
        'flex items-center gap-2 flex-wrap print:hidden',
        // Variant M panes can be as narrow as a phone. `shrink-0` there
        // would keep every button on one max-content line and the pane
        // clips the last/widest one — the rose "Close chart" pill. Let
        // the row take the full pane width and WRAP so Close (and every
        // other button) is always fully visible, never under the edge.
        // Desktop / pane / fullBleed keep `shrink-0` so nothing moves.
        isTabletPaired ? 'basis-full justify-end min-w-0' : 'shrink-0',
      ].join(' ')}
    >
      {/* lg+ — secondary controls inline alongside primary actions.
          Variant M (tabletPaired): the chart pane is ~642 px wide on
          iPad landscape — much narrower than the viewport-based lg:
          breakpoint (1024 px). Forcing inline at lg: would overflow the
          chart pane, clipping `Single/Multi/Tooth/Gum/Adult` off-screen.
          Always collapse behind the ⋮ More menu in tabletPaired. */}
      <div
        className={isTabletPaired
          ? 'hidden'
          : 'hidden lg:flex items-center gap-2 flex-wrap'}
        data-testid="header-secondary-inline"
      >
        {renderSecondary()}
      </div>

      {/* sub-lg (or always, on tabletPaired) — same controls behind a
          ⋮ More overflow menu so the chart header stops overflowing
          its narrow pane. */}
      <div className={isTabletPaired ? '' : 'lg:hidden'}>
        <HeaderOverflowMenu isDark={isDark}>
          {renderSecondary()}
        </HeaderOverflowMenu>
      </div>

      {/* Variant M — `📋 Summary · N` button. Only mounts on
          `tabletPaired` presentation. Sits ahead of Print/Expand so the
          dentist sees the per-tooth shortcut as a peer of the close pill.
          Solid teal background satisfies the day/dark button-visibility
          rule from CLAUDE.md. */}
      {isTabletPaired && (
        <button
          type="button"
          onClick={onSummaryToggle || (() => {})}
          aria-pressed={!!summaryOpen}
          className={[
            'inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md border',
            summaryOpen
              ? 'bg-teal-600 border-teal-700 text-white hover:bg-teal-700'
              : 'border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/40 text-teal-800 dark:text-teal-100 hover:bg-teal-100 dark:hover:bg-teal-900/60',
          ].join(' ')}
          title={summaryOpen ? 'Hide per-tooth summary' : 'Open per-tooth summary'}
        >
          <span aria-hidden="true">📋</span>
          <span>Summary · {summaryCount}</span>
        </button>
      )}

      {/* Always-visible primary actions — Print + Expand + Close stay
          reachable on every breakpoint regardless of which secondary
          controls are shown. */}
      <button
        type="button"
        onClick={onPrint || (() => window.print())}
        className="px-2 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
        title="Print or save as PDF (A4 or A5/A6 card)"
      >
        🖨 Print
      </button>

      <button
        type="button"
        onClick={onToggleExpanded}
        className="px-2 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
        title={expanded ? 'Collapse chart' : 'Expand chart to full screen'}
        aria-label={expanded ? 'Collapse' : 'Expand'}
      >
        {expanded ? '❐' : '⛶'}
      </button>

      {/* Variant M — high-contrast rose close pill in tabletPaired mode.
          Separated from Print/Expand by a thin divider so it reads as the
          exit rather than another secondary control. ≥ 44 px tap target
          for gloved-finger use. Desktop / legacy modes keep the small ✕. */}
      {isTabletPaired ? (
        <>
          <span
            aria-hidden="true"
            className="inline-block w-px h-6 mx-0.5 bg-slate-200 dark:bg-slate-700"
          />
          <button
            type="button"
            onClick={onClose}
            className={[
              'inline-flex items-center gap-1 rounded-lg px-3.5 py-2 text-sm font-semibold shadow-sm',
              'bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white',
              'focus:outline-none focus:ring-2 focus:ring-rose-300 dark:focus:ring-rose-700',
            ].join(' ')}
            aria-label="Close chart"
            title="Close chart"
          >
            <span aria-hidden="true">✕</span>
            <span>Close chart</span>
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={onClose}
          className="px-2 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
          aria-label="Close"
        >
          ✕
        </button>
      )}
    </div>
  );
}


/**
 * HeaderOverflowMenu — ⋮ More dropdown for tablet (and narrower)
 * breakpoints. Shows the secondary controls stacked vertically with
 * touch-friendly spacing. Closes on outside click + on Escape.
 */
function HeaderOverflowMenu({ isDark, children }) {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          'px-2 py-1 text-xs rounded-md border min-w-[36px]',
          'border-slate-300 dark:border-slate-600',
          'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200',
          'hover:bg-slate-50 dark:hover:bg-slate-700',
        ].join(' ')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More chart options"
        title="More options"
      >
        ⋮ More
      </button>

      {open && (
        <div
          role="menu"
          className={[
            'absolute right-0 mt-1 z-50 min-w-[14rem] p-2 rounded-md border shadow-lg',
            'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100',
            'border-slate-300 dark:border-slate-600',
            'flex flex-col gap-2',
          ].join(' ')}
          onClick={(e) => {
            // Close after the inner control reacts to its own click.
            // Use a microtask so the inner handler runs first.
            queueMicrotask(() => {
              if (!(e.target instanceof HTMLElement)) return;
              const tag = e.target.tagName;
              if (tag === 'BUTTON' || tag === 'OPTION') setOpen(false);
            });
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
